import React from 'react';
import { COMPANY_INFO } from '../constants';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const InfoBox = ({ children }) => (
  <div className="bg-red-50 p-6 rounded-lg border border-red-200 mb-8 text-sm space-y-1">
    {children}
  </div>
);

const PolicyLink = ({ href, children }) => (
  <a href={href} className="text-red-600 hover:underline">{children}</a>
);

const SectionHeading = ({ children }) => (
  <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-6">{children}</h2>
);

const BulletList = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1 text-gray-600">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

// ─── About ────────────────────────────────────────────────────────────────────

export const About = () => (
  <div className="max-w-4xl mx-auto px-4 py-12">
    <h1 className="text-4xl font-bold text-gray-900 mb-8">About StoveShare</h1>

    <InfoBox>
      <p><strong>Business:</strong> {COMPANY_INFO.legalName}</p>
      <p><strong>Website:</strong> <PolicyLink href={COMPANY_INFO.website}>{COMPANY_INFO.website}</PolicyLink></p>
      <p><strong>Contact:</strong> <PolicyLink href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</PolicyLink></p>
    </InfoBox>

    <div className="space-y-8 text-gray-700">
      <div>
        <SectionHeading>Our Mission</SectionHeading>
        <p>StoveShare is Australia's premier marketplace platform bridging the gap between food creators and commercial kitchen spaces. We empower culinary entrepreneurs by providing access to professional-grade kitchen facilities while helping kitchen owners monetize unused capacity.</p>
      </div>
      <div>
        <SectionHeading>For Food Creators</SectionHeading>
        <BulletList items={[
          'Chefs & Culinary Artists: Access professional kitchens by the hour, day, or week',
          'Food Startups & Small Businesses: Scale operations without overhead',
          'Bakers & Caterers: Find specialised equipment and facilities',
          'Pop-up Ventures: Flexible booking options for temporary food businesses',
        ]} />
      </div>
      <div>
        <SectionHeading>For Kitchen Owners</SectionHeading>
        <BulletList items={[
          'Restaurants: Monetize downtime and unused capacity',
          'Commercial Kitchen Operators: Maximise facility utilisation',
          'Community Centres & Venues: Generate additional revenue',
          'Food Incubators: Provide flexible access to multiple entrepreneurs',
        ]} />
      </div>
      <div>
        <SectionHeading>Compliance & Standards</SectionHeading>
        <BulletList items={[
          'Food Safety Compliance: All listed kitchens maintain current food business registrations',
          'Insurance Requirements: Minimum $20M public & product liability for hosts, $10M for renters',
          'WHS Compliance: Workplace health and safety standards rigorously enforced',
          'Privacy Protection: Full compliance with Australian Privacy Principles (APPs)',
        ]} />
      </div>
    </div>
  </div>
);

// ─── Privacy Policy ───────────────────────────────────────────────────────────

export const PrivacyPolicy = () => (
  <div className="max-w-4xl mx-auto px-4 py-12">
    <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>

    <InfoBox>
      <p><strong>Business:</strong> {COMPANY_INFO.legalName}</p>
      <p><strong>Effective Date:</strong> {COMPANY_INFO.effectiveDate}</p>
      <p><strong>Contact:</strong> <PolicyLink href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</PolicyLink></p>
    </InfoBox>

    <div className="space-y-6 text-gray-700 text-sm">
      <p>We collect and handle personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).</p>

      <div>
        <SectionHeading>What We Collect</SectionHeading>
        <BulletList items={[
          'Identity & Contact: Name, email, phone, address, ABN, role/title',
          'Business & Compliance: Licences, insurance certificates, inductions',
          'Booking & Transaction: Searches, listings viewed, booking history, invoices',
        ]} />
      </div>

      <div>
        <SectionHeading>How We Use Your Information</SectionHeading>
        <BulletList items={[
          'Provide, operate and improve the Platform',
          'Verify identity and eligibility',
          'Process payments and issue invoices',
          'Communicate updates and security alerts',
          'Marketing with consent (opt out any time)',
        ]} />
      </div>

      <div>
        <SectionHeading>Your Rights</SectionHeading>
        <BulletList items={[
          'Know what information we collect and why',
          'Access and correct your personal information',
          'Complain about privacy breaches',
          'Opt out of direct marketing',
        ]} />
      </div>

      <div>
        <SectionHeading>Complaints</SectionHeading>
        <p>Contact: <PolicyLink href={`mailto:${COMPANY_INFO.supportEmail}`}>{COMPANY_INFO.supportEmail}</PolicyLink></p>
      </div>
    </div>
  </div>
);

// ─── Terms of Service ─────────────────────────────────────────────────────────

export const TermsOfService = () => (
  <div className="max-w-4xl mx-auto px-4 py-12">
    <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
    <p className="text-sm text-gray-500 mb-8">Last updated: 15/08/2026</p>

    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 text-red-800 text-sm">
      Please read these Terms of Service carefully before using StoveShare. By creating an account, listing a kitchen, making a booking, or otherwise using the StoveShare platform, you agree to these Terms.
    </div>

    <div className="space-y-6 text-gray-700 text-sm">

      <div>
        <SectionHeading>1. Definitions</SectionHeading>
        <div className="space-y-2">
          {[
            ['Account', 'An account registered with StoveShare.'],
            ['Booking', 'A confirmed reservation made through the Platform for access to a Kitchen during a specified period.'],
            ['Host', 'A person or business that lists a Kitchen on the Platform.'],
            ['Kitchen', 'A kitchen or food preparation space listed by a Host, including any equipment, fixtures, facilities and utilities made available as part of the Listing.'],
            ['Listing', 'Information provided by a Host about a Kitchen and published on the Platform.'],
            ['Platform', 'The StoveShare website, applications and related services.'],
            ['Renter', 'A person or business that makes or requests a Booking to access a Kitchen.'],
            ['Services', 'The marketplace and related services provided by StoveShare.'],
            ['You', 'A person or entity using the Platform, whether as a Host or Renter.'],
          ].map(([term, def]) => (
            <p key={term}><strong>{term}:</strong> {def}</p>
          ))}
        </div>
      </div>

      <div>
        <SectionHeading>2. Our Role</SectionHeading>
        <p className="mb-2">StoveShare operates an online marketplace that enables Hosts to list Kitchens and Renters to discover and book those Kitchens.</p>
        <p className="font-semibold mb-2">StoveShare is a platform provider only.</p>
        <p className="mb-2">StoveShare does not own, lease, operate, manage or control the Kitchens listed on the Platform and is not responsible for the day-to-day operation of any Kitchen. StoveShare does not provide food preparation, catering, food manufacturing, storage, hospitality or other services conducted by Hosts or Renters. Unless expressly stated otherwise, StoveShare does not inspect, certify, approve or guarantee the condition, safety, suitability, legality or regulatory compliance of any Kitchen, Listing, Host or Renter.</p>
        <p>Any agreement or arrangement concerning the use of a Kitchen is between the Host and the Renter. Hosts and Renters are responsible for independently assessing whether a Kitchen, Booking or proposed activity is suitable for their needs.</p>
      </div>

      <div>
        <SectionHeading>3. Eligibility and Accounts</SectionHeading>
        <BulletList items={[
          'You must be at least 18 years old and have legal capacity to enter into these Terms.',
          'You must provide accurate and current information when creating and using your Account.',
          'You are responsible for maintaining the confidentiality of your Account credentials and for activities conducted through your Account.',
          'StoveShare may suspend or terminate an Account where it reasonably considers this necessary to protect the Platform, its users or comply with applicable law.',
        ]} />
      </div>

      <div>
        <SectionHeading>4. Host Responsibilities</SectionHeading>
        <p className="mb-2">Hosts are solely responsible for the Kitchens they list on StoveShare. A Host must:</p>
        <BulletList items={[
          'Ensure the Kitchen is legally permitted to be used for the activities represented in the Listing.',
          'Comply with all applicable laws, regulations, permits, licences and approvals, including food safety, public health, workplace health and safety, fire safety, planning and building requirements.',
          'Ensure that information provided in the Listing is accurate, complete and not misleading.',
          'Ensure that the Kitchen and equipment are reasonably suitable for the activities represented in the Listing.',
          'Maintain appropriate insurance required by law and reasonably appropriate for the Kitchen.',
          'Provide Renters with any relevant safety instructions, access requirements and reasonable rules for use of the Kitchen.',
          'Promptly notify StoveShare of any material change affecting the safety, legality or availability of the Kitchen.',
          'Comply with confirmed Bookings unless cancellation is reasonably necessary due to safety, legal, operational or other circumstances beyond the Host\'s reasonable control.',
        ]} />
        <p className="mt-2">StoveShare does not assume responsibility for verifying that a Host has complied with these obligations.</p>
      </div>

      <div>
        <SectionHeading>5. Renter Responsibilities</SectionHeading>
        <p className="mb-2">Renters are solely responsible for their activities and use of a Kitchen. A Renter must:</p>
        <BulletList items={[
          'Comply with all applicable laws, regulations, permits, licences and approvals relevant to their activities.',
          'Maintain insurance required by law and reasonably appropriate for their activities.',
          'Use the Kitchen only for lawful purposes and in accordance with the Listing and any reasonable Host rules.',
          'Comply with applicable food safety, hygiene, workplace health and safety and other regulatory requirements.',
          'Use equipment, facilities and utilities appropriately and safely.',
          'Ensure that people attending the Kitchen are authorised and appropriately supervised.',
          'Keep the Kitchen reasonably clean and tidy.',
          'Return the Kitchen and equipment in substantially the condition in which they were provided, except for reasonable wear and tear.',
          'Promptly notify the Host and StoveShare of any material damage, safety issue, contamination, injury, loss or other Incident.',
          'Not engage in unlawful, dangerous, fraudulent or prohibited activities.',
        ]} />
        <p className="mt-2">The Renter is responsible for obtaining any permits, registrations, licences or approvals required for its own business or activities.</p>
      </div>

      <div>
        <SectionHeading>6. Bookings</SectionHeading>
        <BulletList items={[
          'Bookings made through the Platform are subject to the Listing, these Terms and any applicable booking or cancellation policy.',
          'A Booking is confirmed when confirmation is issued through the Platform or otherwise confirmed by StoveShare or the Host.',
          'The Host is responsible for making the Kitchen available for the confirmed Booking.',
          'The Renter is responsible for attending during the agreed Booking period and complying with the Host\'s reasonable requirements.',
          'StoveShare does not guarantee that a particular Kitchen will remain available, suitable or operational.',
        ]} />
      </div>

      <div>
        <SectionHeading>7. Fees and Payments</SectionHeading>
        <BulletList items={[
          'Fees payable for a Booking will be displayed through the Platform or otherwise communicated before confirmation.',
          'StoveShare may charge service fees, commissions or other fees as disclosed to users.',
          'Where StoveShare facilitates payments, payment processing may be provided through third-party payment providers and may be subject to their terms.',
          'Hosts are responsible for their own tax, business, accounting and reporting obligations arising from their activities on the Platform.',
        ]} />
      </div>

      <div>
        <SectionHeading>8. Cancellations and Refunds</SectionHeading>
        <p>Cancellations and refunds are governed by the applicable cancellation and refund policy presented to the user at the time of Booking. StoveShare may assist with processing refunds or resolving cancellation issues but does not guarantee a refund except where required by the applicable policy or law. StoveShare may cancel, suspend or prevent a Booking where it reasonably considers this necessary because of safety concerns, suspected fraud, unlawful activity, material breach of these Terms, technical issues or other circumstances affecting the Platform.</p>
      </div>

      <div>
        <SectionHeading>9. Safety and Legal Compliance</SectionHeading>
        <p className="mb-2">Hosts and Renters are independently responsible for complying with all laws and regulations applicable to their activities. This may include requirements relating to:</p>
        <BulletList items={[
          'Food safety and hygiene',
          'Allergens and food contamination',
          'Workplace health and safety',
          'Fire and emergency safety',
          'Equipment operation',
          'Waste disposal',
          'Building and planning requirements',
          'Licences, permits and registrations',
          'Insurance',
        ]} />
        <p className="mt-2">StoveShare does not provide legal, food safety, regulatory or insurance advice and does not certify that a Kitchen or activity complies with any particular regulatory requirement. Users should obtain independent professional advice where required.</p>
      </div>

      <div>
        <SectionHeading>10. Damage, Incidents and Claims</SectionHeading>
        <p className="mb-2">Any damage, injury, loss, contamination, theft, safety incident or other material event associated with a Booking should be reported promptly to the relevant Host or Renter and to StoveShare where appropriate. Hosts and Renters are responsible for resolving claims arising from their own acts, omissions, negligence, breach of law or breach of these Terms. Where applicable, parties should first seek recovery through their own insurance.</p>
        <p className="font-semibold">StoveShare is not responsible for damage, loss, injury, contamination, theft, business interruption or other consequences arising from the use or operation of a Kitchen or from activities conducted by a Host or Renter, except to the extent liability cannot lawfully be excluded.</p>
        <p className="mt-2">StoveShare may, at its discretion, assist parties in communicating about an Incident but does not act as an insurer, investigator, arbitrator or guarantor of either party.</p>
      </div>

      <div>
        <SectionHeading>11. Listings and Information</SectionHeading>
        <BulletList items={[
          'Hosts are responsible for the accuracy of their Listings.',
          'StoveShare does not guarantee that Listings are accurate, complete, current or suitable for a particular purpose.',
          'Photographs, descriptions, availability, equipment details and other information are provided by Hosts and may change.',
          'Renters should make their own enquiries before making a Booking if they require information that is important to their intended use.',
        ]} />
      </div>

      <div>
        <SectionHeading>12. Prohibited Conduct</SectionHeading>
        <p className="mb-2">You must not use the Platform to:</p>
        <BulletList items={[
          'Engage in unlawful or fraudulent activity',
          'Provide false or misleading information',
          'Damage or misuse a Kitchen or its equipment',
          'Circumvent applicable Platform fees',
          'Interfere with the operation or security of the Platform',
          'Access or collect Platform data without authorisation',
          'Use another person\'s Account without permission',
          'Engage in activities that create an unreasonable risk to people or property',
          'Otherwise breach these Terms or applicable law',
        ]} />
        <p className="mt-2">StoveShare may remove Listings, restrict access or suspend or terminate Accounts where it reasonably considers this necessary.</p>
      </div>

      <div>
        <SectionHeading>13. Reviews and User Content</SectionHeading>
        <p className="mb-2">Users may be able to submit reviews, photographs, descriptions or other content to the Platform. You are responsible for ensuring that content you submit is accurate, lawful and does not infringe another person's rights. By submitting content to StoveShare, you grant StoveShare a non-exclusive, royalty-free licence to use, reproduce and display that content for operating, promoting and improving the Platform, subject to our Privacy Policy. StoveShare may remove content that it reasonably considers inappropriate, unlawful, misleading or inconsistent with these Terms.</p>
      </div>

      <div>
        <SectionHeading>14. Privacy</SectionHeading>
        <p>Personal information collected through the Platform will be handled in accordance with the StoveShare Privacy Policy and applicable Australian privacy laws. By using the Platform, you acknowledge that StoveShare may share information with service providers where reasonably necessary to provide and operate the Platform.</p>
      </div>

      <div>
        <SectionHeading>15. Intellectual Property</SectionHeading>
        <p>The Platform, including its software, branding, design, content and materials, is owned by or licensed to StoveShare and is protected by applicable intellectual property laws. You must not reproduce, modify, distribute, reverse engineer or commercially exploit the Platform or its intellectual property without our written permission.</p>
      </div>

      <div>
        <SectionHeading>16. Limitation of Liability</SectionHeading>
        <p className="mb-2">To the maximum extent permitted by law, StoveShare excludes liability for loss, damage, cost or expense arising from:</p>
        <BulletList items={[
          'The condition, suitability, safety or legality of a Kitchen',
          'The acts or omissions of a Host or Renter',
          'Activities conducted at a Kitchen',
          'Food prepared, stored or handled at a Kitchen',
          'Personal injury, property damage or contamination arising from a Booking',
          'Cancellation, interruption or unavailability of a Kitchen',
          'Loss of income, business, profits, goodwill or opportunity',
          'Any indirect or consequential loss',
        ]} />
        <p className="mt-2">Nothing in these Terms excludes, restricts or modifies any consumer guarantee, warranty, right or remedy that cannot lawfully be excluded under the ACL or other applicable law. Where liability can legally be limited, StoveShare's liability will be limited to the maximum extent permitted by law.</p>
      </div>

      <div>
        <SectionHeading>17. Indemnity</SectionHeading>
        <p className="mb-2">To the maximum extent permitted by law, you indemnify StoveShare and its officers, employees and contractors against loss, damage, liability, claim, cost or expense arising from:</p>
        <BulletList items={[
          'Your breach of these Terms',
          'Your breach of applicable law',
          'Your negligence, wilful misconduct or unlawful conduct',
          'Your use or operation of a Kitchen',
          'Your activities conducted through the Platform',
          'Claims made by another person arising from your acts or omissions',
        ]} />
        <p className="mt-2">This indemnity does not apply to the extent that the relevant loss was caused by StoveShare's own negligence, wilful misconduct or other liability that cannot lawfully be excluded.</p>
      </div>

      <div>
        <SectionHeading>18. Suspension and Termination</SectionHeading>
        <p>You may stop using the Platform at any time, subject to completing or resolving any existing Booking or outstanding obligation. StoveShare may suspend or terminate your Account or remove a Listing if you breach these Terms, provide misleading information, fail to make required payments, engage in unlawful or unsafe conduct, or where StoveShare reasonably considers suspension or termination necessary to protect the Platform or its users. Termination does not affect rights or obligations that arose before termination.</p>
      </div>

      <div>
        <SectionHeading>19. Third-Party Services</SectionHeading>
        <p>The Platform may use third-party services, including payment, hosting, communications and technology providers. StoveShare is not responsible for the availability or performance of third-party services to the extent permitted by law. Third-party services may be subject to their own terms and privacy policies.</p>
      </div>

      <div>
        <SectionHeading>20. Changes to These Terms</SectionHeading>
        <p>StoveShare may update these Terms from time to time. Where changes are material, we will take reasonable steps to notify users. Your continued use of the Platform after updated Terms take effect constitutes acceptance of the updated Terms.</p>
      </div>

      <div>
        <SectionHeading>21. General</SectionHeading>
        <p>These Terms are governed by the laws of <strong>Victoria, Australia</strong>. If any provision of these Terms is found to be invalid or unenforceable, that provision will be read down or severed to the extent necessary, without affecting the remaining provisions. These Terms, together with any applicable policies referenced in them, constitute the agreement between you and StoveShare regarding your use of the Platform. Nothing in these Terms creates a partnership, joint venture, employment relationship or agency relationship between StoveShare and a Host or Renter except where expressly stated.</p>
      </div>

      <div>
        <SectionHeading>22. Contact</SectionHeading>
        <p>If you have questions regarding these Terms or the Platform, please contact StoveShare through the contact details provided on the Platform.</p>
        <p className="mt-2"><strong>StoveShare</strong><br />
          <PolicyLink href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</PolicyLink><br />
          <PolicyLink href={COMPANY_INFO.website}>{COMPANY_INFO.website}</PolicyLink>
        </p>
      </div>

    </div>
  </div>
);

// ─── Cancellation Policy ──────────────────────────────────────────────────────

export const CancellationPolicy = () => (
  <div className="max-w-4xl mx-auto px-4 py-12">
    <h1 className="text-4xl font-bold text-gray-900 mb-2">Cancellation &amp; Refund Policy</h1>
    <p className="text-sm text-gray-500 mb-8">Last updated: 15/08/2026</p>

    <InfoBox>
      <p><strong>Contact:</strong> <PolicyLink href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</PolicyLink></p>
    </InfoBox>

    <div className="space-y-6 text-gray-700 text-sm">
      <p>This Cancellation &amp; Refund Policy applies to Bookings made through the StoveShare Platform. It forms part of the StoveShare Terms of Service. Capitalised terms have the meanings given in those Terms.</p>
      <p>StoveShare operates a marketplace connecting Hosts and Renters. StoveShare does not own or operate the Kitchens and does not control the availability, condition or operation of a Kitchen.</p>

      <div>
        <SectionHeading>1. Cancellation Policy</SectionHeading>
        <p className="mb-3">Each Listing will have a cancellation policy selected by the Host. The applicable policy will be displayed to the Renter before a Booking is confirmed.</p>

        <div className="grid gap-4">
          <div className="border border-green-200 rounded-lg p-4 bg-green-50">
            <h3 className="font-semibold text-green-800 mb-2">Flexible</h3>
            <BulletList items={[
              'Full refund where the Renter cancels at least 7 days before the Booking start time.',
              '50% refund where the Renter cancels between 48 hours and 7 days before the Booking start time.',
              'No refund where the Renter cancels less than 48 hours before the Booking start time.',
            ]} />
          </div>
          <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
            <h3 className="font-semibold text-yellow-800 mb-2">Standard</h3>
            <BulletList items={[
              'Full refund where the Renter cancels at least 14 days before the Booking start time.',
              '50% refund where the Renter cancels between 72 hours and 14 days before the Booking start time.',
              'No refund where the Renter cancels less than 72 hours before the Booking start time.',
            ]} />
          </div>
          <div className="border border-red-200 rounded-lg p-4 bg-red-50">
            <h3 className="font-semibold text-red-800 mb-2">Strict</h3>
            <BulletList items={[
              '50% refund where the Renter cancels at least 14 days before the Booking start time.',
              'No refund where the Renter cancels less than 14 days before the Booking start time.',
            ]} />
          </div>
        </div>
        <p className="mt-3">The applicable cancellation policy will be shown to the Renter before payment.</p>
      </div>

      <div>
        <SectionHeading>2. How to Cancel</SectionHeading>
        <p>Cancellations must be submitted through the cancellation functionality provided by the Platform. A message to the Host, email to StoveShare or other informal communication does not by itself cancel a Booking. A cancellation is effective when confirmation is issued through the Platform.</p>
      </div>

      <div>
        <SectionHeading>3. Host Cancellations</SectionHeading>
        <p>If a Host cancels a confirmed Booking, StoveShare may provide the Renter with a refund or, where appropriate, an alternative arrangement or booking credit. StoveShare may take reasonable action in relation to repeated or inappropriate Host cancellations, including restricting or removing a Listing or Account. StoveShare is not responsible for losses incurred by a Renter as a result of a Host cancellation except to the extent required by law.</p>
      </div>

      <div>
        <SectionHeading>4. Safety, Legal or Unforeseen Circumstances</SectionHeading>
        <p>Where a Booking cannot reasonably proceed because the Kitchen is unavailable, unsafe, or cannot lawfully be used for the intended Booking, StoveShare may provide a refund or booking credit. StoveShare may request reasonable information from the Host or Renter where necessary to assess the circumstances. Nothing in this section requires StoveShare to accept liability for circumstances outside its reasonable control.</p>
      </div>

      <div>
        <SectionHeading>5. No-Shows and Early Departure</SectionHeading>
        <p>If a Renter does not attend a Booking, the Booking will generally remain subject to the applicable cancellation policy and no refund will ordinarily be provided. If a Renter leaves before the end of a Booking, unused Booking time is generally non-refundable unless otherwise agreed by the Host or required by law.</p>
      </div>

      <div>
        <SectionHeading>6. Refund Processing</SectionHeading>
        <p>Where a refund is approved, it will generally be returned using the original payment method. The timing of a refund may depend on the payment provider and financial institution. Certain fees may be non-refundable where disclosed at the time of Booking, subject to any rights that cannot legally be excluded.</p>
      </div>

      <div>
        <SectionHeading>7. Consumer Rights</SectionHeading>
        <p>Nothing in this Policy excludes, restricts or modifies any rights or remedies that cannot lawfully be excluded under the Australian Consumer Law or other applicable law.</p>
      </div>

      <div>
        <SectionHeading>8. StoveShare's Role</SectionHeading>
        <p>StoveShare facilitates Bookings and payments through its Platform where applicable. StoveShare does not guarantee that a Kitchen will be available, suitable, safe or operational. StoveShare may assist with communication between Hosts and Renters regarding cancellations but is not responsible for disputes between Hosts and Renters except to the extent required by law.</p>
      </div>

      <div>
        <SectionHeading>9. Policy Changes</SectionHeading>
        <p>StoveShare may update this Policy from time to time. The version applicable to a Booking will generally be the version presented or incorporated at the time the Booking was made, subject to applicable law.</p>
      </div>
    </div>
  </div>
);

// ─── Damage & Incident Policy ─────────────────────────────────────────────────

export const DamagePolicy = () => (
  <div className="max-w-4xl mx-auto px-4 py-12">
    <h1 className="text-4xl font-bold text-gray-900 mb-2">Damage &amp; Incident Policy</h1>
    <p className="text-sm text-gray-500 mb-8">Last updated: 15/08/2026</p>

    <InfoBox>
      <p><strong>Contact:</strong> <PolicyLink href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</PolicyLink></p>
    </InfoBox>

    <p className="text-gray-700 text-sm mb-6">This Policy applies to damage, injury, contamination, loss, theft, equipment failure or other significant incidents arising in connection with a Booking.</p>

    <div className="space-y-6 text-gray-700 text-sm">

      <div>
        <SectionHeading>1. Responsibility</SectionHeading>
        <p className="mb-2">Hosts and Renters are responsible for their own actions, omissions, personnel, property and legal obligations.</p>
        <p>StoveShare provides the Platform and facilitates communication between Hosts and Renters. StoveShare does not operate, supervise or control the Kitchen and does not assume responsibility for incidents occurring at a Kitchen except to the extent required by law.</p>
      </div>

      <div>
        <SectionHeading>2. Reporting an Incident</SectionHeading>
        <p className="mb-2">Users should promptly notify the other party and StoveShare of any significant incident relating to a Booking.</p>
        <p className="mb-2">Serious matters involving immediate risks to health, safety or property should first be reported to the appropriate emergency service or relevant authority where required.</p>
        <p className="mb-2">Where appropriate, users should provide reasonable information such as:</p>
        <BulletList items={[
          'What happened',
          'When and where it occurred',
          'Who was involved',
          'Photographs or other relevant evidence',
          'Details of any immediate action taken',
        ]} />
      </div>

      <div>
        <SectionHeading>3. Damage</SectionHeading>
        <p className="mb-2">A Renter may be responsible for damage caused by the Renter or people attending the Kitchen through the Renter, except for reasonable wear and tear.</p>
        <p className="mb-2">A Host may be responsible for damage or loss caused by the Host's acts or omissions.</p>
        <p>The parties should seek to resolve damage claims directly and may use their respective insurance where appropriate.</p>
      </div>

      <div>
        <SectionHeading>4. Insurance</SectionHeading>
        <p className="mb-2">Hosts and Renters are responsible for maintaining any insurance required by law and any insurance reasonably appropriate for their activities.</p>
        <p>StoveShare does not provide insurance for Hosts, Renters, Kitchens, equipment, food products or business activities unless expressly stated otherwise.</p>
      </div>

      <div>
        <SectionHeading>5. StoveShare Assistance</SectionHeading>
        <p className="mb-2">StoveShare may, at its discretion, assist Hosts and Renters in communicating about an Incident or provide relevant information available through the Platform.</p>
        <p className="mb-2">Any assistance provided by StoveShare does not make StoveShare responsible for determining liability, assessing the amount of a claim, investigating an Incident or resolving a dispute.</p>
        <p>StoveShare may take action against an Account or Listing where an Incident indicates a potential breach of the Terms of Service or a safety or legal concern.</p>
      </div>

      <div>
        <SectionHeading>6. Disputes</SectionHeading>
        <p className="mb-2">Hosts and Renters are primarily responsible for resolving disputes arising from their activities or use of a Kitchen.</p>
        <p>Nothing in this Policy prevents a party from seeking legal, insurance or regulatory assistance where appropriate.</p>
      </div>

      <div>
        <SectionHeading>7. Consumer Rights</SectionHeading>
        <p>Nothing in this Policy excludes, restricts or modifies any rights or remedies that cannot lawfully be excluded under the Australian Consumer Law or other applicable law.</p>
      </div>

    </div>
  </div>
);

// ─── Kitchen Owner Requirements ───────────────────────────────────────────────

export const KitchenOwnerRequirements = () => (
  <div className="max-w-4xl mx-auto px-4 py-12">
    <h1 className="text-4xl font-bold text-gray-900 mb-2">Kitchen Owner Requirements</h1>
    <p className="text-sm text-gray-500 mb-8">Last updated: 15/08/2026</p>

    <InfoBox>
      <p><strong>Contact:</strong> <PolicyLink href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</PolicyLink></p>
    </InfoBox>

    <div className="space-y-6 text-gray-700 text-sm">
      <p>Hosts are responsible for ensuring that their Kitchen and activities comply with all applicable Australian laws and regulations.</p>

      <div>
        <SectionHeading>Host Confirmation</SectionHeading>
        <p className="mb-3">By listing a Kitchen on StoveShare, the Host confirms that:</p>
        <BulletList items={[
          'They are legally entitled to make the Kitchen available to Renters.',
          'The Kitchen may lawfully be used for the activities described in the Listing.',
          'All required licences, registrations, permits and approvals have been obtained and remain current.',
          'The Kitchen complies with applicable food safety, health and safety, fire, building and other legal requirements.',
          'The Listing accurately describes the Kitchen, facilities, equipment, restrictions and permitted uses.',
          'Appropriate insurance is maintained for the Kitchen and activities conducted there.',
          'Renters are provided with any important safety instructions or reasonable rules applicable to the Kitchen.',
          'StoveShare is promptly notified of any material change affecting the availability, safety or legality of the Kitchen.',
        ]} />
      </div>

      <div>
        <SectionHeading>Host Compliance</SectionHeading>
        <p className="mb-3">Hosts are responsible for verifying and maintaining their own compliance.</p>
        <BulletList items={[
          'StoveShare does not inspect, certify or guarantee the compliance, safety, condition or suitability of a Kitchen.',
          'StoveShare may request information or supporting documentation from a Host where reasonably necessary to operate the Platform, respond to a complaint, address a safety concern or comply with applicable law.',
          'StoveShare may suspend or remove a Listing where it reasonably believes that a Kitchen or Host may present a safety, legal or compliance risk.',
          'Hosts remain solely responsible for their Kitchen and their obligations to Renters, regulators, insurers and other third parties.',
        ]} />
      </div>
    </div>
  </div>
);